import { TestData, TestMetadata } from '../types/testy';
import { validateTestData, getValidationErrors } from '../lib/testySchemas';

export class TestLoaderError extends Error {
  constructor(
    message: string,
    public readonly testId?: string,
    public readonly cause?: Error
  ) {
    super(message);
    this.name = 'TestLoaderError';
  }
}

export class TestValidationError extends TestLoaderError {
  constructor(
    message: string,
    public readonly validationErrors: string[],
    testId?: string
  ) {
    super(message, testId);
    this.name = 'TestValidationError';
  }
}

export class TestLoaderService {
  private static readonly BASE_PATH = '/testy';
  private static readonly TEST_FILE_EXTENSION = '.json';

  /**
   * Load a test by ID from the public directory
   * @param testId The test identifier
   * @returns Promise resolving to validated TestData
   * @throws TestLoaderError for network failures or file not found
   * @throws TestValidationError for invalid JSON structure
   */
  async loadTest(testId: string): Promise<TestData> {
    if (!testId || testId.trim() === '') {
      throw new TestLoaderError('Test ID cannot be empty');
    }

    const sanitizedTestId = this.sanitizeTestId(testId);
    const testUrl = `${TestLoaderService.BASE_PATH}/${sanitizedTestId}${TestLoaderService.TEST_FILE_EXTENSION}`;

    try {
      const response = await fetch(testUrl);

      if (!response.ok) {
        if (response.status === 404) {
          throw new TestLoaderError(`Test not found: ${testId}`, testId);
        }
        throw new TestLoaderError(
          `Failed to load test: ${response.status} ${response.statusText}`,
          testId
        );
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new TestLoaderError(
          `Invalid content type: expected JSON, got ${contentType}`,
          testId
        );
      }

      let rawData: unknown;
      try {
        rawData = await response.json();
      } catch (parseError) {
        throw new TestLoaderError(
          'Failed to parse JSON: invalid JSON format',
          testId,
          parseError as Error
        );
      }

      return this.validateTestStructure(rawData, testId);
    } catch (error) {
      if (error instanceof TestLoaderError) {
        throw error;
      }

      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new TestLoaderError(
          'Network error: failed to fetch test data. Please check your connection.',
          testId,
          error as Error
        );
      }

      throw new TestLoaderError(
        `Unexpected error loading test: ${error instanceof Error ? error.message : 'Unknown error'}`,
        testId,
        error as Error
      );
    }
  }

  /**
   * Validate test data structure using Zod schema
   * @param data Raw data to validate
   * @param testId Test ID for error context
   * @returns Validated TestData
   * @throws TestValidationError for validation failures
   */
  validateTestStructure(data: unknown, testId?: string): TestData {
    // Transform norms data from JSON format to TypeScript interface format
    const transformedData = this.transformNormsData(data);

    const result = validateTestData(transformedData);

    if (!result.success) {
      const errors = getValidationErrors(result);
      throw new TestValidationError(
        `Test validation failed: ${errors.length} error(s) found`,
        errors,
        testId
      );
    }

    return result.data!;
  }

  /**
   * Transform norms data from JSON array format to TypeScript interface format
   * @param data Raw test data
   * @returns Transformed data with proper norms structure
   */
  private transformNormsData(data: unknown): unknown {
    if (!data || typeof data !== 'object' || !('norms' in data)) {
      return data;
    }

    const testData = data as any;

    if (!testData.norms || typeof testData.norms !== 'object') {
      return data;
    }

    const transformedNorms = { ...testData.norms };

    // Transform sections norms
    if (transformedNorms.sections && typeof transformedNorms.sections === 'object') {
      const transformedSections: Record<string, any> = {};

      for (const [sectionId, sectionData] of Object.entries(transformedNorms.sections)) {

        if (Array.isArray(sectionData)) {
          // Direct array format: [{ points: 0, percentile: 10 }]
          const points: number[] = [];
          const percentiles: number[] = [];

          for (const norm of sectionData as any[]) {
            if (norm && typeof norm === 'object' && 'points' in norm && 'percentile' in norm) {
              points.push(norm.points);
              percentiles.push(norm.percentile);
            }
          }

          transformedSections[sectionId] = { points, percentiles };
        } else if (sectionData && typeof sectionData === 'object' && 'overall' in sectionData && Array.isArray(sectionData.overall)) {
          // New format with overall array: { overall: [{ points: 0, percentile: 10 }] }
          const points: number[] = [];
          const percentiles: number[] = [];

          for (const norm of sectionData.overall as any[]) {
            if (norm && typeof norm === 'object' && 'points' in norm && 'percentile' in norm) {
              points.push(norm.points);
              percentiles.push(norm.percentile);
            }
          }

          transformedSections[sectionId] = { points, percentiles };
        } else if (sectionData && typeof sectionData === 'object' && 'parts' in sectionData) {
          // Nested format with parts: { parts: { partId: [...] } }
          // We need to aggregate all parts into a single section norm
          const allPoints: number[] = [];
          const allPercentiles: number[] = [];

          for (const [, partNorms] of Object.entries(sectionData.parts || {})) {

            if (Array.isArray(partNorms)) {
              for (const norm of partNorms as any[]) {
                if (norm && typeof norm === 'object' && 'points' in norm && 'percentile' in norm) {
                  allPoints.push(norm.points);
                  allPercentiles.push(norm.percentile);
                }
              }
            }
          }

          // Sort by points to ensure proper order
          const combined = allPoints.map((point, index) => ({ point, percentile: allPercentiles[index] }));
          combined.sort((a, b) => a.point - b.point);

          transformedSections[sectionId] = {
            points: combined.map(item => item.point),
            percentiles: combined.map(item => item.percentile)
          };
        } else if (sectionData && typeof sectionData === 'object' && 'points' in sectionData && 'percentiles' in sectionData) {
          // Already in correct format
          transformedSections[sectionId] = sectionData;
        } else {
          transformedSections[sectionId] = sectionData;
        }
      }

      transformedNorms.sections = transformedSections;
    }

    // Transform overall norms
    if (Array.isArray(transformedNorms.overall)) {
      const points: number[] = [];
      const percentiles: number[] = [];

      for (const norm of transformedNorms.overall as any[]) {
        if (norm && typeof norm === 'object' && 'points' in norm && 'percentile' in norm) {
          points.push(norm.points);
          percentiles.push(norm.percentile);
        }
      }

      transformedNorms.overall = { points, percentiles };
    }

    return {
      ...testData,
      norms: transformedNorms
    };
  }

  /**
   * Get list of available published tests by scanning the public directory
   * Note: This method attempts to load test metadata from known test files
   * In a production environment, this would typically be replaced with an API endpoint
   * @returns Promise resolving to array of TestMetadata for published tests
   */
  async getAvailableTests(): Promise<TestMetadata[]> {
    // Since we can't directly scan the public directory from the browser,
    // we'll try to load from a predefined list of known test IDs
    // In a real implementation, this would be an API endpoint or manifest file
    const knownTestIds = await this.getKnownTestIds();

    const availableTests: TestMetadata[] = [];
    const loadPromises = knownTestIds.map(async (testId) => {
      try {
        const testData = await this.loadTest(testId);

        // Only include published tests
        if (testData.isPublished) {
          const questionCount = testData.sections.reduce(
            (total, section) => total + section.parts.reduce(
              (sectionTotal, part) => sectionTotal + part.questions.length,
              0
            ),
            0
          );

          availableTests.push({
            id: testData.id,
            title: testData.title,
            durationSec: testData.durationSec,
            isPublished: testData.isPublished,
            isFreeSample: testData.isFreeSample,
            questionCount,
            sectionCount: testData.sections.length
          });
        }
      } catch (error) {
        // Log error but don't fail the entire operation for individual test failures
        console.warn(`Failed to load test metadata for ${testId}:`, error);
      }
    });

    await Promise.allSettled(loadPromises);

    // Sort by title for consistent ordering
    return availableTests.sort((a, b) => a.title.localeCompare(b.title));
  }

  /**
   * Get list of known test IDs
   * In a production environment, this would be replaced with an API call or manifest file
   * @returns Promise resolving to array of test IDs to attempt loading
   */
  private async getKnownTestIds(): Promise<string[]> {
    // Try to load a manifest file first, fall back to hardcoded list
    try {
      const manifestResponse = await fetch('/testy/manifest.json');
      if (manifestResponse.ok) {
        const manifest = await manifestResponse.json();
        if (Array.isArray(manifest.tests)) {
          return manifest.tests.map((test: any) => test.id).filter(Boolean);
        }
      }
    } catch (error) {
      // Manifest file doesn't exist or is invalid, continue with fallback
      console.info('Test manifest not found, using fallback test discovery');
    }

    // Fallback: return known test IDs
    // In development, this could include sample tests
    return [
      //'zsv-sample-test',
      'example-math-test',  // Our new example test
      'zsv-test-v1-final', // Your comprehensive ZSV test
      'zsv-template-example', // Template for creating new tests
      'zsv-test-2025-01',
      //'zsv-test-2025-03',
      'ZSV-TEST-01',
      'ZSV-TEST-02',
      'ZSV-TEST-03',
      'zsv-2023-podzim'
    ];
  }

  /**
   * Sanitize test ID to prevent path traversal attacks
   * @param testId Raw test ID
   * @returns Sanitized test ID safe for file system access
   */
  private sanitizeTestId(testId: string): string {
    // Remove any path traversal attempts and invalid characters
    return testId
      .replace(/[^a-zA-Z0-9\-_]/g, '')
      .replace(/^\.+/, '')
      .substring(0, 100); // Limit length
  }

  /**
   * Check if a test exists without fully loading it
   * @param testId Test ID to check
   * @returns Promise resolving to boolean indicating if test exists and is accessible
   */
  async testExists(testId: string): Promise<boolean> {
    try {
      const sanitizedTestId = this.sanitizeTestId(testId);
      const testUrl = `${TestLoaderService.BASE_PATH}/${sanitizedTestId}${TestLoaderService.TEST_FILE_EXTENSION}`;

      const response = await fetch(testUrl, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get basic test metadata without full validation (for performance)
   * @param testId Test ID
   * @returns Promise resolving to basic test metadata
   * @throws TestLoaderError for loading failures
   */
  async getTestMetadata(testId: string): Promise<TestMetadata> {
    const sanitizedTestId = this.sanitizeTestId(testId);
    const testUrl = `${TestLoaderService.BASE_PATH}/${sanitizedTestId}${TestLoaderService.TEST_FILE_EXTENSION}`;

    try {
      const response = await fetch(testUrl);

      if (!response.ok) {
        throw new TestLoaderError(`Failed to load test metadata: ${response.status}`, testId);
      }

      const rawData = await response.json();

      // Basic validation for metadata fields only
      if (!rawData || typeof rawData !== 'object') {
        throw new TestLoaderError('Invalid test data format', testId);
      }

      const { id, title, durationSec, isPublished, isFreeSample, sections } = rawData;

      if (!id || !title || typeof durationSec !== 'number' || typeof isPublished !== 'boolean') {
        throw new TestLoaderError('Missing required metadata fields', testId);
      }

      const questionCount = Array.isArray(sections)
        ? sections.reduce((total: number, section: any) => {
          if (Array.isArray(section.parts)) {
            return total + section.parts.reduce((sectionTotal: number, part: any) => {
              return sectionTotal + (Array.isArray(part.questions) ? part.questions.length : 0);
            }, 0);
          }
          return total;
        }, 0)
        : 0;

      return {
        id,
        title,
        durationSec,
        isPublished: Boolean(isPublished),
        isFreeSample: Boolean(isFreeSample),
        questionCount,
        sectionCount: Array.isArray(sections) ? sections.length : 0
      };
    } catch (error) {
      if (error instanceof TestLoaderError) {
        throw error;
      }
      throw new TestLoaderError(
        `Failed to load test metadata: ${error instanceof Error ? error.message : 'Unknown error'}`,
        testId,
        error as Error
      );
    }
  }
}

// Export singleton instance
export const testLoaderService = new TestLoaderService();